// Range rings layer family.
//
// Extracted from addOverlays.ts as HI-1 batch 5 round 2 (audit 2026-05-22).
// Pure mechanical move — every source, layer, paint expression,
// layout property, and minzoom/maxzoom is byte-identical to its
// pre-extraction form. Source-id idempotency guard preserved.

import type { Map as MlMap } from 'maplibre-gl';
import { EMPTY_FC } from '../MapView';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';

/**
 * Mount the range-rings source + its layers. Idempotent on the source.
 */
export function addRangeRingsLayers(map: MlMap): void {
    if (!map.getSource('range-rings')) {
      map.addSource('range-rings', { type: 'geojson', data: EMPTY_FC });
      map.addLayer({
        id: 'range-rings-line',
        type: 'line',
        source: 'range-rings',
        paint: {
          // Solid bright white. Reads against sectional, satellite, and
          // topo at every zoom; matches ForeFlight's range-ring style.
          'line-color': '#ffffff',
          'line-opacity': 0.85,
          'line-width': 1.2,
        },
      });
      map.addLayer({
        id: 'range-rings-label',
        type: 'symbol',
        source: 'range-rings',
        layout: {
          'symbol-placement': 'point',
          'text-field': ['get', 'label'],
          'text-size': 11,
          'text-font': ['Noto Sans Regular'],
          'text-allow-overlap': true,
          'text-ignore-placement': true,
        },
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': '#0b0d0f',
          'text-halo-width': 1.6,
        },
        filter: ['==', ['geometry-type'], 'Point'],
      });
    }
}

registerLayerFamily({
  id: 'range-rings',
  label: 'Range rings',
  sourceId: 'range-rings',
  layerIds: ['range-rings-line', 'range-rings-label'],
  mount: (map) => addRangeRingsLayers(map),
  visibilitySelector: (l) => l.rangeRings,
  applyState: (map) => {
    // Range rings source is owned by RangeRingsController which calls
    // registerStyleRebuild directly. We only apply visibility here.
    if (!map.getLayer('range-rings-line')) return;
    const show = useLayerVisibilityStore.getState().layers.rangeRings;
    const v = show ? 'visible' : 'none';
    map.setLayoutProperty('range-rings-line', 'visibility', v);
    map.setLayoutProperty('range-rings-label', 'visibility', v);
  },
});
